import React, {Component, ReactElement, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import SearchPageActionArea from '../../../styledComponents/adminPage/SearchPageActionArea'
import {AdminDefaultMediumButton} from '../../../styledComponents/adminPage/Buttons'
import {Box, CircularProgress, Link, TableCell, TableRow} from '@mui/material'
import {PageMainArea} from '../../../components/shared/hoc/withAdminPageTemplate'
import {
    ExtractHistoryListInfo,
    NatGetExtractHistory,
    SearchConditionPageInfo,
    SearchResult,
} from './repository/NatGetExtractHistory'
import NatExtractHistoryRepo from './repository/NatExtractHistoryRepo'
import {EXTRACT_CATEGORY, EXTRACT_STATUS, MESSAGE_TYPE, VALID_OR_NOT} from '../../../CodeMaster'
import {COMMON_CONSTANTS} from '../../../CommonConstants'
import SortablePaginationTable from '../../../styledComponents/adminPage/SortablePaginationTable'
import SortableTableHeader from '../../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTableBody from '../../../styledComponents/adminPage/SortablePaginationTableBody'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {HKConstants} from '../../../HKConstants'
import {formatDateTimeForUpdateDate} from '../../../formatters/formatDate'
import {NatPostExtractHistory} from './repository/NatPostExtractHistory'
import {Messages} from '../../../Messages'
import SearchResultCountDisplay, {
    SearchResultCountDisplayProps,
} from '../../../styledComponents/adminPage/SearchResultCountDisplay'
import {Location} from 'react-router-dom'
import ReactDOM from 'react-dom'

interface Props {
    extractHistoryRepo: NatExtractHistoryRepo
    location: Location
}

interface States {
    extractDisableFlag: boolean
    extractHistoryListInfo: ExtractHistoryListInfo[]
    searchResult: SearchResult
    searchConditionPageInfo: SearchConditionPageInfo
    isLoadCompleted: boolean
}

const KEY_SEARCH_CONDITION = 'adminExtractHistoryPageSearchCondition'
type ExtractHistorySearchConditionPage = { [extractCategoryValue: string]: SearchConditionPageInfo; }

class NatAdminExtractHistoryList extends Component<Props & AdminPagePropsSet, States> {
    intervalId: NodeJS.Timeout | null
    extractCategory: EXTRACT_CATEGORY

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.intervalId = null
        this.extractCategory = EXTRACT_CATEGORY.RESIDENT_USER_LIST

        this.state = {
            extractDisableFlag: true,
            extractHistoryListInfo: [],
            searchResult: {},
            searchConditionPageInfo: this.getSearchConditionPageInfo(),
            isLoadCompleted: false,
        }
        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
        this.handleExtractButtonClick = this.handleExtractButtonClick.bind(this)
        this.extractRequest = this.extractRequest.bind(this)
        this.monitoringExtractHistory = this.monitoringExtractHistory.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
    }

    async componentDidMount(): Promise<void> {
        try {
            await this.searchExtractHistory(this.state.searchConditionPageInfo)
            await this.monitoringExtractHistory()
        } finally {
            this.setState({isLoadCompleted: true})
        }
    }

    componentWillUnmount(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId)
        }
    }

    getSearchConditionPageInfo(): SearchConditionPageInfo {
        const sessionStorageSearchCondition: ExtractHistorySearchConditionPage =
            JSON.parse(SessionStorageHelper.getStringItemWithDefault(KEY_SEARCH_CONDITION, '[]'))
        const lastSearchCondition: SearchConditionPageInfo =
            sessionStorageSearchCondition[this.extractCategory.value]
        if (!lastSearchCondition) {
            return {
                pageNum: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
                page: COMMON_CONSTANTS.FIRST_PAGE,
            }
        } else {
            return lastSearchCondition
        }
    }

    async searchExtractHistory(searchConditionPageInfo: SearchConditionPageInfo): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getExtractHistory(searchConditionPageInfo)
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async getExtractHistory(searchConditionPageInfo: SearchConditionPageInfo): Promise<void> {
        const {extractHistoryRepo} = this.props
        const searchParams = `pageNum=${encodeURIComponent(searchConditionPageInfo.pageNum)}`
            + '&'
            + `page=${encodeURIComponent(searchConditionPageInfo.page)}`

        const searchExtractHistoryResponse: Nullable<NatGetExtractHistory> =
            await this.props.asyncCallFunction(
                extractHistoryRepo.getExtractHistory.bind(
                    extractHistoryRepo,
                    this.extractCategory.value,
                    searchParams,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

        if (searchExtractHistoryResponse && searchExtractHistoryResponse.details) {
            const details = searchExtractHistoryResponse.details
            ReactDOM.flushSync(() => {
                this.setState({
                    extractDisableFlag: details.extractDisableFlag === VALID_OR_NOT.VALID.value,
                    extractHistoryListInfo: details.extractHistoryListInfo,
                    searchResult: details.searchResult,
                }, () => {
                    if (!this.state.extractDisableFlag) {
                        if (this.intervalId) {
                            clearInterval(this.intervalId)
                        }
                        this.props.eventEmitter.clearHeadingAreaMessage()
                    }
                })
            })
        }
    }

    async handleExtractButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPostExtractHistory> = await this.extractRequest()
            if (response && response.requestError === null) {
                await this.props.eventEmitter.showHeadingAreaMessage({
                    title: null,
                    messageLevel: MESSAGE_TYPE.INFORMATION,
                    detail: Messages.IOPCA0013.toString(this.extractCategory.label),
                })
                await this.getExtractHistory(this.state.searchConditionPageInfo)
                await this.monitoringExtractHistory()
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async extractRequest(): Promise<Nullable<NatPostExtractHistory>> {
        await this.props.eventEmitter.showLoading()
        try {
            const {extractHistoryRepo} = this.props
            return await this.props.asyncCallFunction(
                extractHistoryRepo.postExtractRequest.bind(
                    extractHistoryRepo,
                    this.extractCategory.value,
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async monitoringExtractHistory(): Promise<void> {
        if (this.state.extractDisableFlag) {
            this.intervalId = setInterval(
                async () => {
                    await this.getExtractHistory(this.state.searchConditionPageInfo)
                }, HKConstants.VMN.RES.RESIDENT_USER_LIST_RELOAD_TIME)
        }
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchConditionPageInfo: SearchConditionPageInfo = this.state.searchConditionPageInfo
        searchConditionPageInfo.page = newPage
        await this.searchExtractHistory(
            searchConditionPageInfo,
        )
        this.setState({
                searchConditionPageInfo: searchConditionPageInfo,
            },
            () => {
                this.props.eventEmitter.scrollToContentsTop()
            })
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()
        const searchConditionPageInfo: ExtractHistorySearchConditionPage = {}
        searchConditionPageInfo[this.extractCategory.value] = {
            pageNum: Number(event.target.value),
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }
        SessionStorageHelper.setItem(KEY_SEARCH_CONDITION, JSON.stringify(searchConditionPageInfo))
        await this.searchExtractHistory(
            searchConditionPageInfo[this.extractCategory.value],
        )
        this.setState({
                searchConditionPageInfo: searchConditionPageInfo[this.extractCategory.value],
            },
            () => {
                this.props.eventEmitter.scrollToContentsTop()
            })
    }

    getOutputFile(extractStatus: string, fileName: string, path: string): Nullable<ReactNode> {
        let outputFile: ReactNode = null
        switch (extractStatus) {
            case EXTRACT_STATUS.DONE.value:
                outputFile = <Link href={path} underline="hover">{fileName}</Link>
                break
            case EXTRACT_STATUS.REQUEST.value:
            case EXTRACT_STATUS.PROCESSING.value:
                outputFile = <CircularProgress size={25}/>
                break
            case EXTRACT_STATUS.FAILED.value:
                outputFile = '抽出できませんでした'
                break
            default:
                break
        }
        return outputFile
    }

    getExtractDatetime(extractStatus: string, extractDatetime: string): string {
        let formatDateExtractDatetime: string = ''
        switch (extractStatus) {
            case EXTRACT_STATUS.DONE.value:
                formatDateExtractDatetime = formatDateTimeForUpdateDate(extractDatetime)
                break
            case EXTRACT_STATUS.REQUEST.value:
            case EXTRACT_STATUS.PROCESSING.value:
                formatDateExtractDatetime = ''
                break
            case EXTRACT_STATUS.FAILED.value:
                formatDateExtractDatetime = ''
                break
            default:
                break
        }
        return formatDateExtractDatetime
    }

    renderExtractHistoryTable(extractHistoryListInfo: ExtractHistoryListInfo[]): Nullable<ReactElement> {
        if (extractHistoryListInfo.length === 0) {
            return null
        }
        const headers = [
            {id: 'outputFile', label: '出力ファイル'},
            {id: 'extractDatetime', label: '抽出日時'},
            {id: 'extractor', label: '抽出者'},
            {id: 'status', label: 'ステータス'},
        ]

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchConditionPageInfo.pageNum ? this.state.searchConditionPageInfo.pageNum : 0,
                    page: this.state.searchConditionPageInfo.page ? this.state.searchConditionPageInfo.page : 0,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                />
                <SortablePaginationTableBody>
                    {extractHistoryListInfo.map(
                        (extractHistory, index) => this.renderExtractHistoryDataRecord(extractHistory, index),
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    renderExtractHistoryDataRecord(extractHistory: ExtractHistoryListInfo, index: number): ReactElement {
        const {fileName, path, extractDatetime, extractFamilyName, extractFirstName, extractStatus} = extractHistory

        return (
            <TableRow key={`${extractHistory.fileName}_${index}`}>
                <TableCell align={'center'}>
                    {this.getOutputFile(extractStatus, fileName, path)}
                </TableCell>
                <TableCell align={'center'}>
                    {this.getExtractDatetime(extractStatus, extractDatetime)}
                </TableCell>
                <TableCell align={'center'}>
                    {extractFamilyName} {extractFirstName}
                </TableCell>
                <TableCell align={'center'}>
                    {EXTRACT_STATUS.codeToEnumConstants(extractStatus).label}
                </TableCell>
            </TableRow>
        )
    }

    renderNoSearchResult(): SearchResultCountDisplayProps {
        if (this.state.extractHistoryListInfo.length === 0) {
            return {noResultMessage: `${this.extractCategory.label}出力履歴は登録されていません。`}
        }

        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: this.state.searchConditionPageInfo.pageNum,
            page: this.state.searchConditionPageInfo.page,
        }
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                <SearchPageActionArea>
                    <AdminDefaultMediumButton
                        disabled={this.state.extractDisableFlag}
                        onClick={this.handleExtractButtonClick}
                    >
                        抽出
                    </AdminDefaultMediumButton>
                    <Box mt={'10px'} mb={'10px'}>
                        <div>ステータスが「抽出要求」または「抽出中」の場合、[抽出]ボタンを押すことができません。</div>
                    </Box>
                </SearchPageActionArea>
                <PageMainArea>
                    {this.renderExtractHistoryTable(this.state.extractHistoryListInfo)}
                </PageMainArea>
                <SearchResultCountDisplay
                    {...this.renderNoSearchResult()}
                />
            </>
        )
    }
}

export default createAdminPage(NatAdminExtractHistoryList)

